import { constants, mediaQueryStyles } from "@newsela/angelou";

import { uiBackgroundColor } from "@/view/utils/userContentUIStyles/userContentUIStyles.style";

const { mediaQuery } = mediaQueryStyles;
const { breakpoints } = constants;
const { xs, sm, md, lg } = breakpoints.BreakpointNames;

const UTILITY_NAV_HEIGHT = "72px";
const PANEL_TAB_HEIGHT = "80px";
// TODO: revisit how we're handling z-index in Lyceum

const READ_ALOUD_STICKY_Z_INDEX = 4;
const STICKY_Z_INDEX = 3;

export const CONTENT_LAYOUT = {
  STICKY_THRESHOLD: "70px",
  HEADER_BOTTOM_HEIGHT: {
    sm: "20px",
    md: "46px",
  },
  PANEL_WIDTH: {
    large: "467px",
    medium: "331px",
    mobile: "769px",
  },
};

export const containerCss = (inline = true) => ({
  display: inline ? "inline-grid" : "grid",
  // Using min-content collapses the second column when the activity panel is closed.
  gridTemplateColumns: "1fr min-content",
  gridTemplateRows: "auto auto auto auto 1fr auto",
  gridTemplateAreas:
    '"top-sticky-bar top-sticky-bar top-sticky-bar" "main-sticky-bar main-sticky-bar main-sticky-bar" "alert alert alert" "header header sidebar" "main main sidebar" "page-bottom page-bottom page-bottom"',
  position: "relative",

  [mediaQuery.upTo(md)]: {
    // We're giving the toolbar a height of 0 when it's at the left of the page so its grid cell
    // isn't visible while still being able to style it from where it relatively exists on the grid.
    gridTemplateColumns: "auto",
    gridTemplateAreas:
      '"top-sticky-bar" "main-sticky-bar" "header" "alert" "main" "page-bottom"',
  },
});

export const unauthContainerCss = {
  display: "inline-grid",
  gridTemplateAreas: '"header" "main" "page-bottom"',
  position: "relative",
};

export const videoPresentModeContainerCss = {
  flex: "1",
  alignContent: "flex-start",
};

export const headerCss = (uiType) => ({
  background: uiBackgroundColor(uiType),
  gridArea: "header",
});

const determineZIndex = (isReadAloudModeActive) => {
  return isReadAloudModeActive ? READ_ALOUD_STICKY_Z_INDEX : STICKY_Z_INDEX;
};

export const stickyBarContainerCss = (
  overrideToTop,
  isReadAloudModeActive,
) => ({
  gridArea: overrideToTop ? "top-sticky-bar" : "main-sticky-bar",
  position: "sticky",
  // Required to allow the useIsStuck hook to detect when the element becomes sticky.
  top: "-1px",
  zIndex: overrideToTop
    ? determineZIndex(isReadAloudModeActive)
    : STICKY_Z_INDEX,
});

export const alertCss = {
  gridArea: "alert",
};

export const innerPollSectionCss = (uiType) => ({
  backgroundColor: uiBackgroundColor(uiType),
  display: "flex",
  justifyContent: "center",
  paddingBottom: "20px",
  position: "relative",

  // adding background color behind secondary actions and the activities panel
  ":before, :after": {
    backgroundColor: uiBackgroundColor(uiType),
    content: '""',
    display: "block",
    height: "100%",
    position: "absolute",
    top: 0,
    width: "100%",
    zIndex: -1,
  },
  ":before": {
    right: "100%",
  },
  ":after": {
    left: "100%",
  },
});

export const mainSectionCss = ({
  hasHeaderImage,
  contentIsVideo,
  isActivityPanelExpanded,
  uiType,
}) => {
  const noTopPadding = hasHeaderImage || contentIsVideo;

  return {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gridArea: "main",
    overflowX: "hidden",
    marginTop: CONTENT_LAYOUT.HEADER_BOTTOM_HEIGHT.md,
    paddingTop: noTopPadding ? null : CONTENT_LAYOUT.HEADER_BOTTOM_HEIGHT.sm,

    ":before": {
      content: '""',
      width: "100%",
      display: "block",
      position: "absolute",
      backgroundColor: uiBackgroundColor(uiType),
      height: contentIsVideo ? 0 : CONTENT_LAYOUT.HEADER_BOTTOM_HEIGHT.md,
      left: "0",
      zIndex: "-1",
      transform: `translateY(${hasHeaderImage ? "-100%" : "-200%"})`,
    },

    [mediaQuery.upTo(xs)]: {
      marginTop: CONTENT_LAYOUT.HEADER_BOTTOM_HEIGHT.sm,
    },

    [mediaQuery.downTo(md)]: {
      marginTop: CONTENT_LAYOUT.HEADER_BOTTOM_HEIGHT.md,
      paddingTop: noTopPadding ? null : CONTENT_LAYOUT.HEADER_BOTTOM_HEIGHT.md,
      // remove overflow for sticky purposes to fill in the bg gap between sections
      overflowX:
        hasHeaderImage && isActivityPanelExpanded ? "inherit" : "hidden",

      ":before": {
        height: CONTENT_LAYOUT.HEADER_BOTTOM_HEIGHT.md,
      },
    },
  };
};

export const videoPresentModeMainSectionCss = {
  marginTop: 0,
  paddingTop: 0,

  [mediaQuery.upTo(xs)]: {
    marginTop: 0,
  },

  [mediaQuery.downTo(md)]: {
    marginTop: 0,
    paddingTop: 0,
  },
};

export const innerMainSectionCss = ({
  isActivityPanelExpanded,
  isVideoPresentModeActive,
}) => {
  return {
    padding: "0 16px",
    width: "100%",

    [mediaQuery.upTo(xs)]: {
      // Hide content behind the panel when it is expanded.
      display:
        isActivityPanelExpanded && !isVideoPresentModeActive
          ? "none"
          : "initial",
    },

    [mediaQuery.downTo(xs)]: {
      padding: "0 64px",
      maxWidth: "544px",
    },

    [mediaQuery.downTo(sm)]: {
      padding: "0 110px",
      maxWidth: "724px",
    },

    [mediaQuery.downTo(sm)]: {
      padding: isActivityPanelExpanded ?? "0 48px 0 48px",
      maxWidth: "initial",
    },
  };
};

export const videoPresentModeInnerMainSectionCss = (
  isVideoPresentModeWithoutQuiz,
) => ({
  padding: 0,

  [mediaQuery.downTo(xs)]: {
    padding: 0,
    maxWidth: "544px",
  },

  [mediaQuery.downTo(sm)]: {
    padding: 0,
    maxWidth: isVideoPresentModeWithoutQuiz ? "calc(100vh + 20%)" : "100%",
  },

  [mediaQuery.downTo(md)]: {
    padding: 0,
  },
});

export const panelWrapperCss = {
  gridArea: "sidebar",
  position: "relative",

  [mediaQuery.upTo(md)]: {
    display: "flex",
    gridArea: "main",
    justifyContent: "center",
  },
};

export const SmallScreenPanelZIndex = 99;

export const panelCss = (
  contentIsVideo,
  hasHeaderImage,
  isActivityPanelExpanded,
  isPresentModeActive,
) => {
  const noTopMargin = hasHeaderImage || contentIsVideo;

  return {
    marginTop: noTopMargin ? null : CONTENT_LAYOUT.HEADER_BOTTOM_HEIGHT.md,
    position: "sticky",
    top: CONTENT_LAYOUT.STICKY_THRESHOLD,
    // At larger screen sizes, the panel will be expanded, and at smaller sizes it will not.
    width: isActivityPanelExpanded ? CONTENT_LAYOUT.PANEL_WIDTH.large : 0,

    [mediaQuery.upTo(md)]: {
      bottom: isPresentModeActive ? "0px" : UTILITY_NAV_HEIGHT,
      height: isActivityPanelExpanded
        ? `calc(100% - ${UTILITY_NAV_HEIGHT})`
        : PANEL_TAB_HEIGHT,
      position: "fixed",
      top: "initial",
      width: "100%",
      zIndex: SmallScreenPanelZIndex,
    },

    [mediaQuery.between(md, lg)]: {
      width: CONTENT_LAYOUT.PANEL_WIDTH.large,
    },

    // The utility nav is not at the bottom of the screen between sm and md.
    [mediaQuery.between(sm, md)]: {
      bottom: 0,
    },

    [mediaQuery.between(xs, md)]: {
      maxWidth: CONTENT_LAYOUT.PANEL_WIDTH.mobile,
      height: isActivityPanelExpanded ? "50vh" : PANEL_TAB_HEIGHT,
      bottom: 0,
    },

    [mediaQuery.upTo(xs)]: {
      bottom: 0,
    },
  };
};

export const videoPresentModePanelCss = (isVideoPresentModeWithoutQuiz) => ({
  display: isVideoPresentModeWithoutQuiz && "none",
  marginTop: 0,
  top: "0px",

  [mediaQuery.upTo(md)]: {
    display: "none",
  },
});

export const pageBottomCss = {
  gridArea: "page-bottom",

  footer: {
    [mediaQuery.between(sm, md)]: {
      paddingBottom: PANEL_TAB_HEIGHT,
    },

    [mediaQuery.upTo(sm)]: {
      marginBottom: `calc(${PANEL_TAB_HEIGHT} + ${UTILITY_NAV_HEIGHT})`,
    },
  },
};
